import { useState } from "react";
import { EmptyBrandState } from "@/components/shared/EmptyBrandState";
import { Button } from "@/components/ui/button";
import { Plus, ShieldAlert } from "lucide-react";
import { useAppData } from "@/contexts/AppDataContext";
import { toast } from "@/hooks/use-toast";
import { AddHeroAsinModal } from "@/components/shared/AddHeroAsinModal";
import { SearchTermsProductCard } from "./SearchTermsProductCard";
import { ProhibitedKeywordsModal } from "./ProhibitedKeywordsModal";
import { useAddHeroProducts, useGetHeroProductList } from "@/resources/product/product.api";
import { handleApiError } from "@/utils";

export function SearchTermsContent() {
  const { selectedBrandId } = useAppData();
  const [addHeroAsinModalOpen, setAddHeroAsinModalOpen] = useState(false);
  const [prohibitedModalOpen, setProhibitedModalOpen] = useState(false);

  const { data: heroProducts = [], isLoading } = useGetHeroProductList(
    selectedBrandId ?? undefined,
  );
  const { mutate: addHeroProducts } = useAddHeroProducts();

  const handleAddHeroAsins = async (asins: string[]) => {
    if (!selectedBrandId) return;
    addHeroProducts(
      { brandId: selectedBrandId, asins },
      {
        onSuccess: () => {
          toast({
            title: "Hero ASIN(s) Added",
            description: `${asins.length} ASIN${asins.length !== 1 ? "s" : ""} added: ${asins.join(", ")}`,
          });
        },
        onError: (error) => handleApiError(error),
      },
    );
  };

  if (!selectedBrandId || (!isLoading && heroProducts.length === 0)) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Search Terms</h1>
          <p className="text-muted-foreground mt-1">Generate and manage backend search terms for each hero product.</p>
        </div>
        <EmptyBrandState pageName="Search Terms" skeleton="search-terms" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Search Terms</h1>
          <p className="text-muted-foreground mt-1">
            Generate and manage backend search terms for each hero product.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline-on-white" onClick={() => setProhibitedModalOpen(true)}>
            <ShieldAlert className="h-4 w-4 mr-2" />
            Prohibited Keywords
          </Button>
          <Button className="bg-primary hover:bg-primary/90" onClick={() => setAddHeroAsinModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Hero ASIN
          </Button>
        </div>
      </div>

      <AddHeroAsinModal
        open={addHeroAsinModalOpen}
        onOpenChange={setAddHeroAsinModalOpen}
        onAddAsins={handleAddHeroAsins}
      />

      <ProhibitedKeywordsModal
        open={prohibitedModalOpen}
        onOpenChange={setProhibitedModalOpen}
        brandId={selectedBrandId}
      />

      <div className="space-y-4">
        {heroProducts.map((product, index) => (
          <SearchTermsProductCard
            key={product.id}
            product={product}
            brandId={selectedBrandId}
            defaultOpen={index === 0}
          />
        ))}
      </div>
    </div>
  );
}
